import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Home, BookOpenText, LogOut, Sparkles, Shield, UserCircle2 } from "lucide-react";
import { clearAuth, getUser, unlockAudioPlayback } from "../lib/api";

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();

  const baseNav = [
    { to: "/dashboard", label: "Home", icon: Home, testId: "nav-home" },
    { to: "/wordlists", label: "Word Lists", icon: BookOpenText, testId: "nav-wordlists" },
    { to: "/profile", label: "Profile", icon: UserCircle2, testId: "nav-profile" },
  ];
  const nav = user?.role === "admin"
    ? [...baseNav, { to: "/admin", label: "Admin", icon: Shield, testId: "nav-admin" }]
    : baseNav;

  const handleLogout = () => { clearAuth(); navigate("/login"); };

  // Resume the Web Audio context on the user's first interaction anywhere
  // in the app. Once resumed, the context stays running for the rest of
  // the page lifetime — required by browser autoplay policies for
  // Listen/Practice/Test auto-play to work.
  useEffect(() => {
    const onGesture = () => { unlockAudioPlayback(); };
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    window.addEventListener("touchstart", onGesture);
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      window.removeEventListener("touchstart", onGesture);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F9FF] bg-polka">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b-4 border-blue-100">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-11 h-11 rounded-2xl bg-[#FBBF24] flex items-center justify-center shadow-[0_4px_0_#B45309]">
              <Sparkles className="w-6 h-6 text-[#1E1B4B]" strokeWidth={3} />
            </div>
            <div className="leading-none">
              <div className="font-display text-2xl font-bold text-[#1E1B4B]">BuzzSpell</div>
              <div className="text-xs font-bold text-[#4D96FF] -mt-0.5">Spelling Bee Fun</div>
            </div>
          </Link>
          <nav className="flex items-center gap-1">
            {nav.map((n) => {
              const Icon = n.icon;
              const active = location.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testId}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-full font-bold text-sm transition-colors ${
                    active ? "bg-[#4D96FF] text-white" : "text-[#1E1B4B] hover:bg-blue-50"
                  }`}
                >
                  <Icon className="w-5 h-5" strokeWidth={3} />
                  <span className="hidden sm:inline">{n.label}</span>
                </Link>
              );
            })}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full ml-2">
              <div className="w-7 h-7 rounded-full bg-[#FF6B6B] text-white font-bold flex items-center justify-center text-sm">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
              <span className="font-bold text-sm text-[#1E1B4B]" data-testid="user-name">{user?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="ml-1 flex items-center gap-2 px-3 py-2.5 rounded-full font-bold text-sm text-[#F43F5E] hover:bg-rose-50 transition-colors"
              title="Log out"
            >
              <LogOut className="w-5 h-5" strokeWidth={3} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-8">
        <Outlet />
      </main>
      <footer className="pt-10 pb-8 text-center text-sm text-slate-500">
        Made with <span className="text-[#FF6B6B]">&hearts;</span> for spelling champs
      </footer>
    </div>
  );
}
